import { PickupPoint } from "@/types/pickupPoint";
import {
  GelProximityBtn,
  GelProximityPickupDetails,
  getElementById,
} from "./elementUtils";
import { PickupPointResult } from "@/types/pickupPointResult";
import { displayPickupError, hidePickupError } from "./pickupErrorHandler";
import CarrierSelectionHandler from "./CarrierSelectionHandler";

class PickupCarrierManager {
  private gelSDK: GelSDK;
  private pickupHandlerMap = new WeakMap<Element, EventListener>();
  private carrierSelectionHandler?: CarrierSelectionHandler;

  constructor(
    gelSDK: GelSDK,
    carrierSelectionHandler?: CarrierSelectionHandler,
  ) {
    this.gelSDK = gelSDK;
    this.carrierSelectionHandler = carrierSelectionHandler;
  }

  public setCarrierSelectionHandler(handler: CarrierSelectionHandler): void {
    this.carrierSelectionHandler = handler;
  }

  private createPickupHandler(): EventListener {
    return (event: Event) => {
      event.preventDefault();

      this.gelSDK.createUIModal({
        callbackOk: (data: PickupPoint) => {
          this.handlePickupPointSelection(data).catch((error) => {
            displayPickupError();
            console.error(error);
          });
        },
        callbackKo: () => {},
      });
    };
  }

  public addPickupCarrierListener(element: Element): void {
    const handler = this.createPickupHandler();
    element.addEventListener("click", handler);
    this.pickupHandlerMap.set(element, handler);
  }

  public removePickupCarrierListener(element: Element): void {
    const handler = this.pickupHandlerMap.get(element);
    if (handler) {
      element.removeEventListener("click", handler);
      this.pickupHandlerMap.delete(element);
    }
  }

  private async handlePickupPointSelection(data: PickupPoint): Promise<void> {
    const response = await this.sendPickupPointToServer(data);

    if (!response.ok) {
      throw new Error(
        `Failed to send pickup point selection: HTTP error ${response.status}: ${response.statusText}`,
      );
    }

    const result = await response.json();
    this.updateDOMWithPickupPointResult(result);
  }

  private sendPickupPointToServer(data: PickupPoint): Promise<Response> {
    const gelConfig = window.gelProximityConfig;
    const params = new URLSearchParams();

    params.append("action", "setPickUpPointForCurrentCart");
    params.append("status", "ok");
    params.append("payload", JSON.stringify(data));

    return fetch(gelConfig.ajaxUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: params.toString(),
    });
  }

  private updateDOMWithPickupPointResult(result: PickupPointResult): void {
    const details = result?.data?.pickupsDetails;

    if (typeof details === "string") {
      const container = getElementById(GelProximityPickupDetails);
      container.innerHTML = details;
      hidePickupError();
      const btn = getElementById(GelProximityBtn);

      // Add "update carrier" button behavior
      this.addPickupCarrierListener(btn);

      this.carrierSelectionHandler?.updateSubmitOrderButtonState();
    } else {
      throw new Error("Unexpected response format:" + JSON.stringify(result));
    }
  }
}

export default PickupCarrierManager;
